import React, { useState, useEffect } from 'react';
import { ShieldCheck, Eye, Ban } from 'lucide-react';
import { Slideout, Button, Pill, Select, Textarea, Label, FieldGroup } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';

/**
 * SkillReviewSlideout — the V2 (no-modal) replacement for the V1 "Review Skill"
 * modal (AgentsPage.jsx DecidePhasePanel). Sets a policy/native skill's autonomy
 * by writing a SkillReview row via the SHARED backend `api.reviewSkill` — the same
 * endpoint the native Let Decide tab uses (it does not branch on archetype), so
 * this works identically for Let / ExteLet / AutoLet / SysLet.
 *
 * Decision → backend reviewStatus: autonomous → promoted, human_approval →
 * reviewed, blocked → blocked. The Decide→Act gate requires a non-blocked row.
 *
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {string}   props.agentId
 * @param {Object}   props.skill        needs id, name, evalScore, riskLevel, reviewStatus
 * @param {Function} props.onReviewed   called after a successful review (parent reloads)
 */
export default function SkillReviewSlideout({ isOpen, onClose, agentId, skill, onReviewed }) {
  const toastCtx = useToast();
  const addToast = (arg) => toastCtx?.addToast?.(arg);

  const [decision, setDecision] = useState('human_approval');
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Seed the form from the latest review whenever a new skill opens.
  useEffect(() => {
    if (isOpen && skill) {
      const rs = (skill.reviewStatus || '').toLowerCase();
      setDecision(rs === 'promoted' ? 'autonomous' : rs === 'blocked' ? 'blocked' : rs === 'reviewed' ? 'human_approval' : 'human_approval');
      setJustification('');
    }
  }, [isOpen, skill]);

  if (!skill) return null;

  const evalNum = typeof skill.evalScore === 'number'
    ? (skill.evalScore <= 1 ? Math.round(skill.evalScore * 100) : Math.round(skill.evalScore))
    : null;
  const scoreStr = evalNum == null ? 'N/A' : `${evalNum}%`;
  const autonomousBlocked = decision === 'autonomous' && (evalNum == null || evalNum < 90);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.reviewSkill?.(agentId, skill.id, { decision, justification: justification.trim() });
      if (res && res.ok === false) throw new Error(res.error || res.data?.error || 'Review failed');
      addToast({ tone: decision === 'blocked' ? 'warning' : 'success',
        title: `${skill.name} reviewed`, description: `decision: ${decision}` });
      onReviewed?.();
      onClose?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Review failed', description: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const OPTIONS = [
    { value: 'autonomous', label: 'Autonomous', icon: ShieldCheck, desc: 'No-touch enforcement. Requires eval ≥ 90%.' },
    { value: 'human_approval', label: 'Human Approval', icon: Eye, desc: 'Each call surfaces for a human to approve.' },
    { value: 'blocked', label: 'Block Skill', icon: Ban, desc: 'Skill is blocked from promotion.' },
  ];

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Review skill"
      width={460}
      header={
        <div>
          <div className="text-[13.5px] font-bold text-v2-ink">Review Skill</div>
          <div className="text-[11.5px] text-v2-ink-muted truncate">{skill.name}</div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={submit} loading={submitting}
            disabled={submitting || autonomousBlocked}>
            Confirm Review
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-v2-md bg-v2-muted px-3 py-2 text-[12px]">
          <span className="text-v2-ink-muted">Eval Score:</span>
          <strong className="text-v2-ink">{scoreStr}</strong>
          {skill.riskLevel && (
            <>
              <span className="text-v2-ink-muted ml-2">Risk:</span>
              <Pill tone={skill.riskLevel === 'critical' || skill.riskLevel === 'high' ? 'danger'
                : skill.riskLevel === 'medium' ? 'warning' : 'muted'} size="sm">{skill.riskLevel}</Pill>
            </>
          )}
        </div>

        <FieldGroup>
          <Label>Autonomy decision</Label>
          <div className="space-y-2">
            {OPTIONS.map((o) => {
              const Icon = o.icon;
              const active = decision === o.value;
              return (
                <button key={o.value} type="button" onClick={() => setDecision(o.value)}
                  className={'w-full text-left rounded-v2-md border px-3 py-2.5 v2-focus-ring transition-colors '
                    + (active ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border-subtle bg-v2-surface hover:border-v2-action')}>
                  <span className="flex items-center gap-2">
                    <Icon size={14} className={o.value === 'blocked' ? 'text-v2-danger' : 'text-v2-ink-secondary'} />
                    <span className="text-[12.5px] font-semibold text-v2-ink">{o.label}</span>
                  </span>
                  <span className="block text-[11px] text-v2-ink-muted mt-1 leading-relaxed">{o.desc}</span>
                </button>
              );
            })}
          </div>
          {autonomousBlocked && (
            <div className="text-[11px] text-v2-danger mt-1">
              Autonomous requires eval ≥ 90% (current: {scoreStr}). Raise the score or choose Human Approval.
            </div>
          )}
        </FieldGroup>

        <FieldGroup>
          <Label>Justification</Label>
          <Textarea rows={3} placeholder="Explain the reasoning for this decision…"
            value={justification} onChange={(e) => setJustification(e.target.value)} />
        </FieldGroup>
      </div>
    </Slideout>
  );
}
